import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Col, Row, Form, Overlay, Popover, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";

import { withNavigation } from "../../../../utils/wrapper";
import Address from "../../../../layout/address";
import CodeSelect from "../../../../layout/select/code";

const INTENT_CODES = [
  { value: "plaintiff", description: "Plaintiff" },
  { value: "defendant", description: "Defendant" },
  { value: "both", description: "Both" },
];

const toDateOrNull = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

function normalizeComparable(value) {
  if (value == null) return "";
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value).trim();
}

function toDisplayDate(value) {
  if (!value) return null;

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

function formatSuggestionValue(value, path = "") {
  if (value == null || value === "") return "[blank]";

  const lowerPath = String(path || "").toLowerCase();

  if (lowerPath.includes("date")) {
    return toDisplayDate(value) || String(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function Biographical(props) {
  const {
    LastCohabitation,
    Location,
    marriageDate,
    separationDate,
    livingTogether,
    intentDate,
    intentCode,
    agreementDate,
    onChange,
    suggestions,
    onApplySuggestion,
    onRejectSuggestion,
  } = props;

  const [lastCohabitationState, setLastCohabitationState] = useState(LastCohabitation ?? {});
  const [locationState, setLocationState] = useState(Location ?? {});
  const [marriageDateState, setMarriageDateState] = useState(toDateOrNull(marriageDate));
  const [separationDateState, setSeparationDateState] = useState(toDateOrNull(separationDate));
  const [livingTogetherState, setLivingTogetherState] = useState(!!livingTogether);
  const [intentDateState, setIntentDateState] = useState(toDateOrNull(intentDate));
  const [intentCodeState, setIntentCodeState] = useState(intentCode ?? "plaintiff");
  const [agreementDateState, setAgreementDateState] = useState(toDateOrNull(agreementDate));

  const [suggestionMenu, setSuggestionMenu] = useState({
    show: false,
    target: null,
    path: null,
  });

  const lastEmittedRef = useRef(null);

  useEffect(() => setLastCohabitationState(LastCohabitation ?? {}), [LastCohabitation]);
  useEffect(() => setLocationState(Location ?? {}), [Location]);
  useEffect(() => setMarriageDateState(toDateOrNull(marriageDate)), [marriageDate]);
  useEffect(() => setSeparationDateState(toDateOrNull(separationDate)), [separationDate]);
  useEffect(() => setLivingTogetherState(!!livingTogether), [livingTogether]);
  useEffect(() => setIntentDateState(toDateOrNull(intentDate)), [intentDate]);
  useEffect(() => setIntentCodeState(intentCode ?? "plaintiff"), [intentCode]);
  useEffect(() => setAgreementDateState(toDateOrNull(agreementDate)), [agreementDate]);

  const normalizedIntentCode = useMemo(() => {
    const valid = INTENT_CODES.some((c) => c.value === intentCodeState);
    return valid ? intentCodeState : "plaintiff";
  }, [intentCodeState]);

  const currentSnapshot = useMemo(() => ({
    LastCohabitation: lastCohabitationState,
    Location: locationState,
    marriageDate: marriageDateState,
    separationDate: separationDateState,
    livingTogether: livingTogetherState,
    intentDate: intentDateState,
    intentCode: normalizedIntentCode,
    agreementDate: agreementDateState,
  }), [
    lastCohabitationState,
    locationState,
    marriageDateState,
    separationDateState,
    livingTogetherState,
    intentDateState,
    normalizedIntentCode,
    agreementDateState,
  ]);

  const emitChange = useCallback(
    (overrides = {}) => {
      if (!onChange) return;

      const payload = {
        LastCohabitation: overrides.LastCohabitation ?? lastCohabitationState,
        Location: overrides.Location ?? locationState,
        marriageDate: overrides.marriageDate ?? marriageDateState,
        separationDate: overrides.separationDate ?? separationDateState,
        livingTogether: overrides.livingTogether ?? livingTogetherState,
        intentDate: overrides.intentDate ?? intentDateState,
        intentCode: overrides.intentCode ?? normalizedIntentCode,
        agreementDate: overrides.agreementDate ?? agreementDateState,
      };

      lastEmittedRef.current = payload;
      onChange(payload);
    },
    [
      onChange,
      lastCohabitationState,
      locationState,
      marriageDateState,
      separationDateState,
      livingTogetherState,
      intentDateState,
      normalizedIntentCode,
      agreementDateState,
    ]
  );

  useEffect(() => {
    if (!onChange) return;

    const payload = {
      LastCohabitation: lastCohabitationState,
      Location: locationState,
      marriageDate: marriageDateState,
      separationDate: separationDateState,
      livingTogether: livingTogetherState,
      intentDate: intentDateState,
      intentCode: normalizedIntentCode,
      agreementDate: agreementDateState,
    };

    const last = lastEmittedRef.current;
    if (
      last &&
      last.LastCohabitation === payload.LastCohabitation &&
      last.Location === payload.Location &&
      last.marriageDate === payload.marriageDate &&
      last.separationDate === payload.separationDate &&
      last.livingTogether === payload.livingTogether &&
      last.intentDate === payload.intentDate &&
      last.intentCode === payload.intentCode &&
      last.agreementDate === payload.agreementDate
    ) {
      return;
    }

    onChange(payload);
  }, [
    onChange,
    lastCohabitationState,
    locationState,
    marriageDateState,
    separationDateState,
    livingTogetherState,
    intentDateState,
    normalizedIntentCode,
    agreementDateState,
  ]);

  const closeSuggestionMenu = useCallback(() => {
    setSuggestionMenu({
      show: false,
      target: null,
      path: null,
    });
  }, []);

  const openSuggestionMenu = useCallback((event, path) => {
    event.preventDefault();
    event.stopPropagation();
    setSuggestionMenu({
      show: true,
      target: event.currentTarget,
      path,
    });
  }, []);

  const hasSuggestion = useCallback((path) => {
    const incoming = suggestions?.[path];
    if (incoming === undefined) return false;

    const current = currentSnapshot?.[path];
    return normalizeComparable(current) !== normalizeComparable(incoming);
  }, [suggestions, currentSnapshot]);

  const applySuggestion = useCallback(() => {
    const path = suggestionMenu.path;
    if (!path) return;

    const value = suggestions?.[path];
    if (onApplySuggestion) onApplySuggestion(path, value);
    closeSuggestionMenu();
  }, [suggestionMenu.path, suggestions, onApplySuggestion, closeSuggestionMenu]);

  const rejectSuggestion = useCallback(() => {
    const path = suggestionMenu.path;
    if (!path) return;

    const value = suggestions?.[path];
    if (onRejectSuggestion) onRejectSuggestion(path, value);
    closeSuggestionMenu();
  }, [suggestionMenu.path, suggestions, onRejectSuggestion, closeSuggestionMenu]);

  const renderLabel = useCallback((text, path) => {
    const showIcon = hasSuggestion(path);

    return (
      <Form.Label>
        {text}
        {showIcon ? (
          <button
            type="button"
            onClick={(e) => openSuggestionMenu(e, path)}
            style={{
              border: 0,
              background: "transparent",
              padding: 0,
              marginLeft: 6,
              lineHeight: 1,
              fontSize: "0.95rem",
              cursor: "pointer",
            }}
            title="Review imported survey value"
          >
            ✨
          </button>
        ) : null}
      </Form.Label>
    );
  }, [hasSuggestion, openSuggestionMenu]);

  const activeSuggestionValue = suggestionMenu.path
    ? suggestions?.[suggestionMenu.path]
    : undefined;
  
  const applyNestedSuggestion = useCallback((prefix, path, value) => {
    if (!onApplySuggestion) return;
    onApplySuggestion(`${prefix}.${path}`, value);
  }, [onApplySuggestion]);

  const rejectNestedSuggestion = useCallback((prefix, path, value) => {
    if (!onRejectSuggestion) return;
    onRejectSuggestion(`${prefix}.${path}`, value);
  }, [onRejectSuggestion]);

  return (
    <Col xs="12">
      <Row className="g-3">
        <Col lg="12">
          <h2 className="small-title">About the Marriage</h2>
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="4">
          <div className="mb-3 top-label">
            <DatePicker
              isClearable
              id="marriageDate"
              name="marriageDate"
              className="form-control"
              selected={marriageDateState}
              onChange={(date) => {
                setMarriageDateState(date);
                emitChange({ marriageDate: date });
              }}
            />
            {renderLabel("MARRIED ON", "marriageDate")}
          </div>
        </Col>

        <Col md="4">
          <div className="mb-3 top-label">
            <DatePicker
              isClearable
              id="separationDate"
              name="separationDate"
              className="form-control"
              selected={separationDateState}
              onChange={(date) => {
                setSeparationDateState(date);
                emitChange({ separationDate: date });
              }}
            />
            {renderLabel("SEPARATED ON", "separationDate")}
          </div>
        </Col>

        <Col md="4">
          <Form.Check
            type="switch"
            id="livingTogether"
            label={
              <>
                Living Together?
                {hasSuggestion("livingTogether") ? (
                  <button
                    type="button"
                    onClick={(e) => openSuggestionMenu(e, "livingTogether")}
                    style={{
                      border: 0,
                      background: "transparent",
                      padding: 0,
                      marginLeft: 6,
                      lineHeight: 1,
                      fontSize: "0.95rem",
                      cursor: "pointer",
                    }}
                    title="Review imported survey value"
                  >
                    ✨
                  </button>
                ) : null}
              </>
            }
            checked={livingTogetherState}
            style={{ marginTop: "15px" }}
            onChange={(e) => {
              const v = e.target.checked;
              setLivingTogetherState(v);
              emitChange({ livingTogether: v });
            }}
          />
        </Col>
      </Row>

      <Row className="g-3">
        <Col md="4">
          <div className="mb-3 top-label">
            <DatePicker
              isClearable
              id="intentDate"
              name="intentDate"
              className="form-control"
              selected={intentDateState}
              onChange={(date) => {
                setIntentDateState(date);
                emitChange({ intentDate: date });
              }}
            />
            {renderLabel("FORMED INTENTION ON", "intentDate")}
          </div>
        </Col>

        <Col md="4">
          <div className="mb-3 top-label">
            <CodeSelect
              codes={INTENT_CODES}
              value={normalizedIntentCode}
              onChange={(e) => {
                const v = e?.value ?? "plaintiff";
                setIntentCodeState(v);
                emitChange({ intentCode: v });
              }}
              placeholder=""
              name="intentCode"
            />
            {renderLabel("INTENTION BY", "intentCode")}
          </div>
        </Col>

        <Col md="4">
          <div className="mb-3 top-label">
            <DatePicker
              isClearable
              id="agreementDate"
              name="agreementDate"
              className="form-control"
              selected={agreementDateState}
              onChange={(date) => {
                setAgreementDateState(date);
                emitChange({ agreementDate: date });
              }}
            />
            {renderLabel("AGREEMENT DATE", "agreementDate")}
          </div>
        </Col>
      </Row>

      <Row className="g-3">
        <Col lg="12">
          <h2 className="small-title">Married In</h2>
        </Col>
      </Row>

      <Address
        {...locationState}
        name="Location"
        id="Location"
        simple
        suggestions={suggestions?.Location || {}}
        onApplySuggestion={(path, value) => applyNestedSuggestion("Location", path, value)}
        onRejectSuggestion={(path, value) => rejectNestedSuggestion("Location", path, value)}
        onChange={(values) => {
          const v = values ?? {};
          setLocationState(v);
          emitChange({ Location: v });
        }}
      />

      <Row className="g-3">
        <Col lg="12">
          <h2 className="small-title">Last Lived Together At</h2>
        </Col>
      </Row>

      <Address
        {...lastCohabitationState}
        name="LastCohabitation"
        id="LastCohabitation"
        suggestions={suggestions?.LastCohabitation || {}}
        onApplySuggestion={(path, value) => applyNestedSuggestion("LastCohabitation", path, value)}
        onRejectSuggestion={(path, value) => rejectNestedSuggestion("LastCohabitation", path, value)}
        onChange={(values) => {
          const v = values ?? {};
          setLastCohabitationState(v);
          emitChange({ LastCohabitation: v });
        }}
      />

      <Overlay
        target={suggestionMenu.target}
        show={suggestionMenu.show}
        placement="bottom-end"
        rootClose
        onHide={closeSuggestionMenu}
      >
        {(overlayProps) => (
          <Popover {...overlayProps}>
            <Popover.Header as="h3">Imported survey value</Popover.Header>
            <Popover.Body>
              <div className="small text-muted mb-2">{suggestionMenu.path}</div>
              <div className="mb-3" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {formatSuggestionValue(activeSuggestionValue, suggestionMenu.path)}
              </div>
              <div className="d-flex gap-2">
                <Button size="sm" variant="primary" onClick={applySuggestion}>
                  Accept
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={rejectSuggestion}>
                  Reject
                </Button>
              </div>
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
    </Col>
  );
}

Biographical.propTypes = {
  LastCohabitation: PropTypes.object,
  Location: PropTypes.object,
  marriageDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  separationDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  livingTogether: PropTypes.bool,
  intentDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  intentCode: PropTypes.string,
  agreementDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onChange: PropTypes.func,
  suggestions: PropTypes.object,
  onApplySuggestion: PropTypes.func,
  onRejectSuggestion: PropTypes.func,
};

Biographical.defaultProps = {
  LastCohabitation: {},
  Location: {},
  marriageDate: null,
  separationDate: null,
  livingTogether: false,
  intentDate: null,
  intentCode: "plaintiff",
  agreementDate: null,
  onChange: null,
  suggestions: {},
  onApplySuggestion: null,
  onRejectSuggestion: null,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(connect(mapStateToProps, null, null, { forwardRef: true })(Biographical));